import { msg } from "@lingui/core/macro";
import { createMemo, Show } from "solid-js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import { cn, computeCollectionStats, getCollectionQuery, queryKeys } from "@famidex/shared";
import { BarChart3 } from "lucide-solid";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@/utils/number";
import { BarChart, type ChartSegment, DonutChart } from "@/components/ui/mini-chart";

interface StatsSectionProps {
  userId: string;
}

export function StatsSection(props: StatsSectionProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const collection = createPowerSyncQuery({
    queryKey: () => queryKeys.collection.items(props.userId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getCollectionQuery(dbInstance, props.userId) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const stats = createMemo(() => computeCollectionStats(collection.data ?? []));
  // % de la colección seguida que ya está en posesión (vs. sólo deseada).
  const ownedPct = () => {
    const s = stats();
    return s.total > 0 ? Math.round((s.owned / s.total) * 100) : 0;
  };

  const donutSegments = (): ChartSegment[] => [
    { label: t(msg`Owned`), value: stats().owned, color: "var(--success)" },
    { label: t(msg`Wishlist`), value: stats().wishlist, color: "var(--warning)" },
  ];

  const barSegments = (): ChartSegment[] => [
    { label: t(msg`Owned`), value: stats().owned, color: "var(--success)" },
    { label: t(msg`Wishlist`), value: stats().wishlist, color: "var(--warning)" },
    { label: t(msg`Duplicates`), value: stats().duplicate, color: "var(--accent)" },
  ];

  const donutSummary = () => {
    const { owned, total } = stats();
    const pct = ownedPct();
    return t(msg`${owned} of ${total} items owned (${pct}%)`);
  };

  return (
    <Card>
      <CardHeader>
        <div class="flex items-center gap-2">
          <BarChart3 class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Statistics`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-6">
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label={t(msg`Total`)}
            value={stats().total}
            colorClass="text-primary"
            bgClass="bg-primary/10"
            isLoading={collection.isPending}
          />
          <StatCard
            label={t(msg`Owned`)}
            value={stats().owned}
            colorClass="text-success"
            bgClass="bg-success/10"
            isLoading={collection.isPending}
          />
          <StatCard
            label={t(msg`Wishlist`)}
            value={stats().wishlist}
            colorClass="text-warning"
            bgClass="bg-warning/10"
            isLoading={collection.isPending}
          />
          <StatCard
            label={t(msg`Duplicates`)}
            value={stats().duplicate}
            colorClass="text-foreground"
            bgClass="bg-muted/50"
            isLoading={collection.isPending}
          />
        </div>

        <Show when={stats().total > 0 && !collection.isPending}>
          <div class="grid grid-cols-1 items-center gap-6 sm:grid-cols-[auto_1fr]">
            {/* Ownership donut: owned vs wishlist */}
            <div class="flex flex-col items-center gap-3">
              <DonutChart
                segments={donutSegments()}
                centerValue={`${ownedPct()}%`}
                centerLabel={t(msg`Owned`)}
                summary={donutSummary()}
              />
              <div class="flex items-center gap-4 text-xs">
                <span class="flex items-center gap-1.5">
                  <span class="h-2.5 w-2.5 rounded-full bg-success" />
                  <span class="text-muted-foreground">{t(msg`Owned`)}</span>
                </span>
                <span class="flex items-center gap-1.5">
                  <span class="h-2.5 w-2.5 rounded-full bg-warning" />
                  <span class="text-muted-foreground">{t(msg`Wishlist`)}</span>
                </span>
              </div>
            </div>

            {/* Breakdown bars */}
            <div class="space-y-3">
              <span class="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t(msg`Breakdown`)}
              </span>
              <BarChart
                segments={barSegments()}
                summary={barSegments()
                  .map((s) => `${s.label}: ${s.value}`)
                  .join(", ")}
              />
            </div>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}

function StatCard(props: {
  label: string;
  value: number;
  colorClass: string;
  bgClass: string;
  isLoading?: boolean;
}) {
  const { locale } = useI18n();
  const isCompact = () => props.value >= COMPACT_THRESHOLD;
  const formattedValue = () => formatCompactNumber(props.value, locale());
  const fullLabel = () => `${props.value.toLocaleString(locale())} ${props.label.toLowerCase()}`;

  return (
    <div
      class={cn(
        "flex flex-col items-center justify-center rounded-2xl p-4 text-center transition-all hover:scale-[1.02]",
        props.bgClass,
      )}
    >
      <Show
        when={props.isLoading}
        fallback={
          <Show
            when={isCompact()}
            fallback={
              <span
                class={cn(
                  "text-2xl font-black leading-none tracking-tight select-none",
                  props.colorClass,
                )}
                aria-label={fullLabel()}
              >
                {props.value}
              </span>
            }
          >
            <Tooltip openDelay={350} closeDelay={100}>
              <TooltipTrigger class="focus-visible:outline-none select-none cursor-help">
                <span
                  class={cn(
                    "text-2xl font-black leading-none tracking-tight border-b border-dashed border-current pb-0.5",
                    props.colorClass,
                  )}
                  aria-label={fullLabel()}
                >
                  {formattedValue()}
                </span>
              </TooltipTrigger>
              <TooltipContent class="max-w-[200px] text-center font-semibold bg-background text-foreground border border-border/40 shadow-lg px-2.5 py-1.5 rounded-lg select-none">
                {fullLabel()}
              </TooltipContent>
            </Tooltip>
          </Show>
        }
      >
        <Skeleton class="h-7 w-8" />
      </Show>
      <div class="mt-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-80 select-none">
        {props.label}
      </div>
    </div>
  );
}
